import { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileDown, Mail, CheckCircle2 } from "lucide-react";
import { contentAPI } from "../lib/api";
import { generateCalculatorPdf } from "../lib/generatePdf";

/**
 * EmailGatePdfButton — captures email into newsletter list, then generates + downloads
 * a branded PDF report of the calculation result. Offers two paths:
 *  1. Enter email → get PDF (primary, builds email list)
 *  2. "Skip — just download" (secondary, zero-friction option)
 */
export const EmailGatePdfButton = ({ pdfPayload, filename, testid }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (withEmail) => {
    setLoading(true);
    try {
      if (withEmail && email) {
        try {
          await contentAPI.subscribe(email);
        } catch (e) {
          // Don't block PDF on subscription failure
        }
      }
      generateCalculatorPdf({ ...pdfPayload, filename });
      setDone(true);
      toast.success(withEmail ? "PDF downloaded. You're on the briefing list." : "PDF downloaded.");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="border-2 border-primary bg-primary/5 p-6 flex items-center gap-4"
        data-testid={`${testid}-success`}
      >
        <CheckCircle2 className="w-8 h-8 text-primary shrink-0" />
        <div>
          <p className="font-heading text-lg font-semibold">PDF downloaded.</p>
          <p className="font-body text-sm text-foreground/70">
            Check your downloads folder for <span className="font-mono">{filename}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-foreground text-background border-2 border-foreground p-6 md:p-8"
      data-testid={testid || "email-gate-pdf"}
    >
      <div className="flex items-start gap-3 mb-4">
        <FileDown className="w-6 h-6 text-primary shrink-0 mt-1" />
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 block mb-1">
            Take This With You
          </span>
          <h3 className="font-heading text-xl font-semibold">
            Download a printable PDF of this analysis
          </h3>
          <p className="font-body text-sm text-background/80 mt-2">
            Great for your tax preparer, financial planner, or your records.
            Optionally join our free weekly OBBBA briefing — no spam, unsubscribe anytime.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-0 mt-5">
        <div className="flex-1 relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-background/50" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com (optional)"
            className="w-full bg-background text-foreground pl-10 pr-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid={`${testid}-email`}
          />
        </div>
        <button
          onClick={() => submit(!!email)}
          disabled={loading}
          className="bg-primary text-primary-foreground font-mono uppercase tracking-widest text-xs px-6 py-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid={`${testid}-download`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {email ? "Get PDF + Subscribe" : "Download PDF"}
        </button>
      </div>
      <button
        onClick={() => submit(false)}
        disabled={loading}
        className="font-mono text-[10px] uppercase tracking-widest text-background/50 hover:text-background/80 mt-4 underline"
        data-testid={`${testid}-skip`}
      >
        Skip email — just download
      </button>
    </div>
  );
};
